/**
 * Edição do documento do fluxo — funções puras: recebem o documento e
 * devolvem outro. O builder só muda o rascunho por aqui.
 *
 * A saída padrão (`from_item_id: null`) é a saída do card: só vale no último
 * bloco. Quando o último bloco muda (bloco novo no fim, apagado, movido), a
 * ligação acompanha o novo último bloco — ou some, se ele encerra o caminho.
 */
import { blockPorts, branchesOf, createBlock, isTerminal, itemsOf, newBranch, newChoiceItem } from './blocks';
import { newId, takenIds } from './ids';
import type { Block, BlockType, DocEdge, DocGroup, FlowDoc, FlowDocument } from './types';

// ───────────── documento da API ⇄ rascunho ─────────────

function normalizeBlock(b: Block): Block {
  switch (b.type) {
    case 'input_choice':
      return { ...b, items: [...(b.items ?? [])] };
    case 'condition':
      return { ...b, branches: (b.branches ?? []).map((br) => ({ ...br, rules: [...(br.rules ?? [])] })) };
    case 'visit_request':
      return { ...b, availability_variables: [...(b.availability_variables ?? [])] };
    case 'webhook':
      return { ...b, variables: [...(b.variables ?? [])] };
    default:
      return { ...b };
  }
}

/** O documento da API com todas as listas presentes. */
export function normalizeDoc(raw: FlowDocument): FlowDoc {
  return {
    start_group_id: raw.start_group_id,
    groups: (raw.groups ?? []).map((g) => ({
      id: g.id,
      title: g.title ?? '',
      x: g.x ?? 0,
      y: g.y ?? 0,
      blocks: (g.blocks ?? []).map(normalizeBlock),
    })),
    edges: (raw.edges ?? []).map((e) => ({
      id: e.id,
      from_block_id: e.from_block_id,
      from_item_id: e.from_item_id ?? null,
      to_group_id: e.to_group_id,
    })),
    variables: (raw.variables ?? []).map((v) => ({ name: v.name })),
  };
}

// ───────────── consultas ─────────────

export function findGroup(doc: FlowDoc, groupId: string): DocGroup | undefined {
  return doc.groups.find((g) => g.id === groupId);
}

export function locateBlock(
  doc: FlowDoc,
  blockId: string,
): { group: DocGroup; index: number; block: Block } | null {
  for (const group of doc.groups) {
    const index = group.blocks.findIndex((b) => b.id === blockId);
    if (index >= 0) return { group, index, block: group.blocks[index]! };
  }
  return null;
}

/** A ligação que sai desta porta, se houver. */
export function edgeFrom(doc: FlowDoc, blockId: string, itemId: string | null): DocEdge | undefined {
  return doc.edges.find((e) => e.from_block_id === blockId && e.from_item_id === itemId);
}

const mapGroup = (doc: FlowDoc, groupId: string, fn: (g: DocGroup) => DocGroup): FlowDoc => ({
  ...doc,
  groups: doc.groups.map((g) => (g.id === groupId ? fn(g) : g)),
});

/**
 * Depois de mexer nos blocos de um card: a saída do card (ligação padrão do
 * antigo último bloco) passa para o novo último bloco — ou some, se ele não
 * tem saída padrão (encerra o caminho) ou se o card ficou vazio.
 */
function keepGroupExit(before: FlowDoc, after: FlowDoc, groupId: string): FlowDoc {
  const prev = findGroup(before, groupId);
  const next = findGroup(after, groupId);
  if (!prev || !next) return after;
  const prevLast = prev.blocks[prev.blocks.length - 1];
  const nextLast = next.blocks[next.blocks.length - 1];
  if (prevLast?.id === nextLast?.id) return after;

  const exit = prevLast ? edgeFrom(after, prevLast.id, null) : undefined;
  if (!exit) return after;
  // Uma porta, uma ligação: a saída do card substitui uma padrão antiga do novo último.
  const edges = after.edges.filter(
    (e) => e !== exit && !(e.from_block_id === nextLast?.id && e.from_item_id === null),
  );
  const canExit = !!nextLast && blockPorts(nextLast, true).some((p) => p.itemId === null);
  return canExit
    ? { ...after, edges: [...edges, { ...exit, from_block_id: nextLast.id }] }
    : { ...after, edges };
}

// ───────────── cards ─────────────

export function addGroup(
  doc: FlowDoc,
  position: { x: number; y: number },
  blocks: Block[] = [],
  title = '',
): { doc: FlowDoc; groupId: string } {
  const taken = takenIds(doc);
  const groupId = newId('g', taken);
  const n = doc.groups.length + 1;
  const group: DocGroup = {
    id: groupId,
    title: title || `Card ${n}`,
    x: Math.round(position.x),
    y: Math.round(position.y),
    blocks,
  };
  const next: FlowDoc = { ...doc, groups: [...doc.groups, group] };
  // Primeiro card de um fluxo vazio: é onde a conversa começa.
  if (!doc.groups.some((g) => g.id === doc.start_group_id)) next.start_group_id = groupId;
  return { doc: next, groupId };
}

export function updateGroup(
  doc: FlowDoc,
  groupId: string,
  patch: Partial<Pick<DocGroup, 'title' | 'x' | 'y'>>,
): FlowDoc {
  return mapGroup(doc, groupId, (g) => ({ ...g, ...patch }));
}

export function setStart(doc: FlowDoc, groupId: string): FlowDoc {
  return findGroup(doc, groupId) ? { ...doc, start_group_id: groupId } : doc;
}

/**
 * Apaga o card, as ligações que saem dele e as que chegam nele. Blocos
 * "Pular para card" que apontavam para ele ficam sem destino; se era o
 * início, o primeiro card que sobrar vira o início.
 */
export function deleteGroup(doc: FlowDoc, groupId: string): FlowDoc {
  const group = findGroup(doc, groupId);
  if (!group) return doc;
  const blockIds = new Set(group.blocks.map((b) => b.id));
  const groups = doc.groups
    .filter((g) => g.id !== groupId)
    .map((g) =>
      g.blocks.some((b) => b.type === 'jump' && b.group_id === groupId)
        ? {
            ...g,
            blocks: g.blocks.map((b) =>
              b.type === 'jump' && b.group_id === groupId ? { ...b, group_id: '' } : b,
            ),
          }
        : g,
    );
  return {
    ...doc,
    groups,
    edges: doc.edges.filter((e) => e.to_group_id !== groupId && !blockIds.has(e.from_block_id)),
    start_group_id: doc.start_group_id === groupId ? (groups[0]?.id ?? '') : doc.start_group_id,
  };
}

/** Copia o card ao lado: ids novos para card, blocos, opções e condições; as saídas vão junto. */
export function duplicateGroup(doc: FlowDoc, groupId: string): { doc: FlowDoc; groupId: string } {
  const group = findGroup(doc, groupId);
  if (!group) return { doc, groupId };
  const taken = takenIds(doc);
  const fresh = (prefix: 'g' | 'b' | 'e' | 'i' | 'c') => {
    const id = newId(prefix, taken);
    taken.add(id);
    return id;
  };
  const blockMap = new Map<string, string>();
  const itemMap = new Map<string, string>();
  const blocks = group.blocks.map((b): Block => {
    const id = fresh('b');
    blockMap.set(b.id, id);
    if (b.type === 'input_choice') {
      return {
        ...b,
        id,
        items: itemsOf(b).map((it) => {
          const itemId = fresh('i');
          itemMap.set(`${b.id}:${it.id}`, itemId);
          return { ...it, id: itemId };
        }),
      };
    }
    if (b.type === 'condition') {
      return {
        ...b,
        id,
        branches: branchesOf(b).map((br) => {
          const branchId = fresh('c');
          itemMap.set(`${b.id}:${br.id}`, branchId);
          return { ...br, id: branchId, rules: (br.rules ?? []).map((r) => ({ ...r })) };
        }),
      };
    }
    return { ...normalizeBlock(b), id };
  });
  const copyId = fresh('g');
  const copy: DocGroup = {
    id: copyId,
    title: group.title ? `${group.title} (cópia)` : '',
    x: group.x + 40,
    y: group.y + 40,
    blocks,
  };
  const edges = doc.edges
    .filter((e) => blockMap.has(e.from_block_id))
    .map((e) => ({
      ...e,
      id: fresh('e'),
      from_block_id: blockMap.get(e.from_block_id)!,
      from_item_id:
        e.from_item_id === null
          ? null
          : (itemMap.get(`${e.from_block_id}:${e.from_item_id}`) ?? e.from_item_id),
    }));
  return { doc: { ...doc, groups: [...doc.groups, copy], edges: [...doc.edges, ...edges] }, groupId: copyId };
}

// ───────────── blocos ─────────────

/** Bloco novo no card (no fim, ou em `index`). */
export function addBlock(
  doc: FlowDoc,
  groupId: string,
  type: BlockType,
  index?: number,
): { doc: FlowDoc; blockId: string } {
  const block = createBlock(type, takenIds(doc));
  const next = insertBlock(doc, groupId, block, index);
  return { doc: next, blockId: block.id };
}

export function insertBlock(doc: FlowDoc, groupId: string, block: Block, index?: number): FlowDoc {
  const after = mapGroup(doc, groupId, (g) => {
    const blocks = [...g.blocks];
    blocks.splice(index ?? blocks.length, 0, block);
    return { ...g, blocks };
  });
  return keepGroupExit(doc, after, groupId);
}

/**
 * Troca o bloco pela versão editada. Opções e condições que deixaram de
 * existir levam junto as ligações que saíam delas.
 */
export function updateBlock(doc: FlowDoc, next: Block): FlowDoc {
  const at = locateBlock(doc, next.id);
  if (!at) return doc;
  const ids = new Set<string>(
    next.type === 'input_choice'
      ? itemsOf(next).map((i) => i.id)
      : next.type === 'condition'
        ? branchesOf(next).map((b) => b.id)
        : [],
  );
  const edges = doc.edges.filter(
    (e) =>
      e.from_block_id !== next.id ||
      e.from_item_id === null ||
      e.from_item_id === 'error' ||
      ids.has(e.from_item_id),
  );
  return {
    ...mapGroup(doc, at.group.id, (g) => ({
      ...g,
      blocks: g.blocks.map((b) => (b.id === next.id ? next : b)),
    })),
    edges,
  };
}

/** Apaga o bloco e as ligações que saem dele (a saída do card passa ao novo último bloco). */
export function deleteBlock(doc: FlowDoc, blockId: string): FlowDoc {
  const at = locateBlock(doc, blockId);
  if (!at) return doc;
  const isLast = at.index === at.group.blocks.length - 1;
  const removed = mapGroup(doc, at.group.id, (g) => ({
    ...g,
    blocks: g.blocks.filter((b) => b.id !== blockId),
  }));
  // A saída do card sobrevive ao último bloco: keepGroupExit a muda de dono.
  const after = {
    ...removed,
    edges: removed.edges.filter((e) => e.from_block_id !== blockId || (isLast && e.from_item_id === null)),
  };
  const fixed = keepGroupExit(doc, after, at.group.id);
  return { ...fixed, edges: fixed.edges.filter((e) => e.from_block_id !== blockId) };
}

/** Sobe (-1) ou desce (+1) o bloco dentro do card. */
export function moveBlock(doc: FlowDoc, blockId: string, direction: -1 | 1): FlowDoc {
  const at = locateBlock(doc, blockId);
  if (!at) return doc;
  const target = at.index + direction;
  if (target < 0 || target >= at.group.blocks.length) return doc;
  const after = mapGroup(doc, at.group.id, (g) => {
    const blocks = [...g.blocks];
    [blocks[at.index], blocks[target]] = [blocks[target]!, blocks[at.index]!];
    return { ...g, blocks };
  });
  return keepGroupExit(doc, after, at.group.id);
}

export function duplicateBlock(doc: FlowDoc, blockId: string): { doc: FlowDoc; blockId: string } {
  const at = locateBlock(doc, blockId);
  if (!at) return { doc, blockId };
  const taken = takenIds(doc);
  const id = newId('b', taken);
  taken.add(id);
  let copy: Block = { ...normalizeBlock(at.block), id };
  if (copy.type === 'input_choice')
    copy = {
      ...copy,
      items: itemsOf(copy).map((i) => ({ ...newChoiceItem(taken, i.label), value: i.value })),
    };
  if (copy.type === 'condition')
    copy = {
      ...copy,
      branches: branchesOf(copy).map((b) => ({ ...b, id: newBranch(taken).id, rules: [...(b.rules ?? [])] })),
    };
  return { doc: insertBlock(doc, at.group.id, copy, at.index + 1), blockId: id };
}

// ───────────── ligações ─────────────

/** Liga a porta a um card. Cada porta tem uma ligação só: a anterior é trocada. */
export function connect(
  doc: FlowDoc,
  from: { blockId: string; itemId: string | null },
  toGroupId: string,
): FlowDoc {
  if (!locateBlock(doc, from.blockId) || !findGroup(doc, toGroupId)) return doc;
  const previous = edgeFrom(doc, from.blockId, from.itemId);
  if (previous?.to_group_id === toGroupId) return doc;
  const edges = doc.edges.filter((e) => e !== previous);
  const edge: DocEdge = {
    id: previous?.id ?? newId('e', takenIds(doc)),
    from_block_id: from.blockId,
    from_item_id: from.itemId,
    to_group_id: toGroupId,
  };
  return { ...doc, edges: [...edges, edge] };
}

export function removeEdges(doc: FlowDoc, edgeIds: Iterable<string>): FlowDoc {
  const gone = new Set(edgeIds);
  return gone.size ? { ...doc, edges: doc.edges.filter((e) => !gone.has(e.id)) } : doc;
}

/** O card onde nasce uma ligação (o card do bloco de origem). */
export function edgeSourceGroup(doc: FlowDoc, edge: DocEdge): DocGroup | undefined {
  return locateBlock(doc, edge.from_block_id)?.group;
}

/** Os blocos que nunca rodam (depois de um terminal) — só para avisar no card. */
export function afterTerminal(group: DocGroup): Set<string> {
  const out = new Set<string>();
  let ended = false;
  for (const b of group.blocks) {
    if (ended) out.add(b.id);
    if (isTerminal(b)) ended = true;
  }
  return out;
}
